<template>
  <Fragment>
    <td class="px-1">
      <px-autocomplete-produto
        dense
        show-details
        menu-props="auto"
        :truncate-value="50"
        :clearable="false"
        v-model="produto.produto"
        :class="`rounded-tag-small ${!produto.especificacaoProduto ? 'required' : ''}`"
        base-path="/produtos/page"
        :error="$v.produto.produto.$error"
        @blur="$v.produto.produto.$touch()"
        @change="onChange(produto)">
        <template v-slot:append v-if="produto">
          <div v-if="produto.produto">
            <px-resumo-produto-dialog :produto="produto.produto"
                                      :unidade-medida="produto.unidadeMedida"/>
          </div>
        </template>
      </px-autocomplete-produto>
    </td>
    <td class="px-1">
      <px-input-decimal-number
        dense
        required
        ref="quantidade"
        label="Quantidade"
        class="required rounded-tag-small"
        :precision="getPrecision(produto.produto)"
        :error="$v.produto.quantidade.$error"
        @blur="$v.produto.quantidade.$touch()"
        v-model="produto.quantidade"/>
    </td>
    <td class="px-1">
      <px-autocomplete-unidade-medida
        dense
        required
        :clearable="false"
        :only-open="false"
        class="required rounded-tag-small"
        :error="$v.produto.unidadeMedida.id.$error"
        @blur="$v.produto.unidadeMedida.id.$touch()"
        v-model="produto.unidadeMedida"/>
    </td>
    <td class="px-1">
      <v-textarea
        outlined
        auto-grow
        dense
        hide-details
        v-model="produto.especificacaoProduto"
        label="Especificação"
        counter="1500"
        rows="1"
        :class="`rounded-tag-small ${!produto.produto ? 'required' : ''}`"
        :error="$v.produto.especificacaoProduto.$error"
        @blur="$v.produto.especificacaoProduto.$touch()"/>
    </td>
    <td class="px-1">
      <px-cadastro-produto-observacoes @file-loading="onFileLoad"
                                       :is-edicao="isEdicao"
                                       :produto="produto"/>
      <px-cadastro-produto-campos-adicionais
        dense
        :show-cadastros-adicionais="showCadastrosAdicionais"
        ref="camposAdicionais"
        :produto="produto"
        cadastros-adicionais-type="requisicao-item"
        :cadastros-adicionais="produto.cadastrosAdicionais"
        :campos-obrigatorios="camposObrigatorios"
        @replicar-campo="onReplicarCampo"/>
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <v-btn icon small v-on="on" @click="duplicar(produto)">
            <v-icon small>mdi-content-copy</v-icon>
          </v-btn>
        </template>
        <span>Copiar produto</span>
      </v-tooltip>
      <v-tooltip top v-if="canRemove">
        <template v-slot:activator="{ on }">
          <v-btn icon
                 small
                 v-on="on"
                 @click="removeProduto">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </template>
        <span>Remover produto</span>
      </v-tooltip>
    </td>
  </Fragment>
</template>

<script>
import { ErpCamposIntegracao, TipoProduto } from 'px-business-components';
import { isEmpty } from 'lodash';
import { required, requiredIf } from 'vuelidate/lib/validators';
import { getPrecision } from '@/utils/produto-utils';
import validationMixin from '@/mixins/validation-mixin';
import { Fragment } from 'vue-frag';
import {
  computeFrom,
  initialStateCamposErp,
  validateCamposErp,
  hasCamposAdicionaisErp,
} from '@/modules/requisicao/cadastro/campos-erp/validate-campos-erp';
import validationFileMixin from '@/mixins/validation-file-mixin';
import { mergeCadastrosAdicionais, extrairCadastrosAdicionais } from '@/utils/cadastros-adicionais-utils';
import TiposEntidadeCadastroAdicional from '@/enums/tipos-entidade-cadastro-adicional';

export default {
  mixins: [validationMixin, validationFileMixin],
  props: {
    produto: {
      type: Object,
      required: true,
    },
    isEdicao: Boolean,
    isRascunho: Boolean,
    camposObrigatorios: Object,
    erpCamposIntegracao: String,
  },
  computed: {
    showCadastrosAdicionais() {
      if (this.erpCamposIntegracao === ErpCamposIntegracao.keys.SENIOR_PFIFFNER.key) {
        return this.produto.produto
          ? this.produto.produto.tipoProduto === TipoProduto.keys.PRODUTO.key : true;
      }
      return this.erpCamposIntegracao !== null;
    },
    getPrecision() {
      return item => getPrecision(item);
    },
    canRemove() {
      return !this.produto.id || this.isRascunho;
    },
  },
  methods: {
    naoNecessitaInicializarCadastros() {
      const { cadastrosAdicionais } = this.produto;
      return cadastrosAdicionais !== null
        && typeof cadastrosAdicionais === 'object'
        && hasCamposAdicionaisErp(this.erpCamposIntegracao, cadastrosAdicionais);
    },
    validate() {
      const isInvalid = [
        this.validateForm(),
        this.validateFile(),
        this.$refs.camposAdicionais.validate(),
        !this.validateCadastrosAdicionais(),
      ].some(v => v);
      return !isInvalid;
    },
    validateCadastrosAdicionais() {
      const [isValid, message] = validateCamposErp(
        this.erpCamposIntegracao,
        this.produto.cadastrosAdicionais,
        this.produto.produto,
      );
      if (!isValid) {
        this.$toast(message, { color: 'error' });
      }
      return isValid;
    },
    onReplicarCampo(campo) {
      this.$emit('replicar-campo', campo);
    },
    onChange(produto) {
      this.produto.especificacaoProduto = this.getEspecificacao(produto);
      this.produto.unidadeMedida = this.getUnidadeMedida(produto);
      this.produto.arquivos = this.getArquivos(produto);
      this.produto.observacaoExterna = this.getObservacaoExterna(produto);
      this.produto.observacaoInterna = this.getObservacaoInterna(produto);
      this.produto.finalidadeCompra = this.getFinalidadeCompra(produto);
      this.inicializarCadastrosAdicionais();
    },
    getUnidadeMedida(produto) {
      return produto.produto ? { ...produto.produto.unidadeMedida } : null;
    },
    getEspecificacao(produto) {
      if (produto.especificacaoProduto) {
        return produto.especificacaoProduto;
      }
      return produto.produto ? produto.produto.especificacao : null;
    },
    getArquivos(produto) {
      if (produto.arquivos && produto.arquivos.length) {
        return produto.arquivos;
      }
      return produto.produto ? produto.produto.arquivos : [];
    },
    getObservacaoExterna(produto) {
      if (produto.observacaoExterna) {
        return produto.observacaoExterna;
      }
      return produto.produto ? produto.produto.observacaoExterna : null;
    },
    getObservacaoInterna(produto) {
      if (produto.observacaoInterna) {
        return produto.observacaoInterna;
      }
      return produto.produto ? produto.produto.observacaoInterna : null;
    },
    getFinalidadeCompra(produto) {
      const finalidadeCompra = produto.produto ? produto.produto.finalidadeCompra : null;
      return finalidadeCompra ?? produto.finalidadeCompra;
    },
    duplicar(item) {
      this.$emit('duplicar-produto', item);
    },
    removeProduto() {
      this.$emit('remove-produto');
    },
    getCadastrosAdicionaisFromProduto(produto) {
      const mainCadastrosAdicionais = extrairCadastrosAdicionais(
        produto.cadastrosAdicionais,
        TiposEntidadeCadastroAdicional.keys.REQUISICAO_PRODUTO.key,
      );
      const produtoCadastrosAdicionais = produto.produto ? produto.produto.cadastrosAdicionais : [];
      return mergeCadastrosAdicionais(mainCadastrosAdicionais, produtoCadastrosAdicionais);
    },
    inicializarCadastrosAdicionais() {
      if (this.naoNecessitaInicializarCadastros()) {
        return;
      }
      const cadastrosAdicionais = this.getCadastrosAdicionaisFromProduto(this.produto);

      if (isEmpty(cadastrosAdicionais)) {
        this.produto.cadastrosAdicionais = initialStateCamposErp(this.erpCamposIntegracao);
        return;
      }

      this.produto.cadastrosAdicionais = computeFrom(
        this.erpCamposIntegracao,
        cadastrosAdicionais,
      );
    },
  },
  created() {
    this.inicializarCadastrosAdicionais();
  },
  validations: {
    produto: {
      produto: {
        required: requiredIf(data => !data.especificacaoProduto),
      },
      especificacaoProduto: {
        required: requiredIf(data => !data.produto),
      },
      quantidade: {
        required,
        minValue: value => value > 0,
      },
      unidadeMedida: {
        id: {
          required,
        },
      },
    },
  },
  components: {
    Fragment,
  },
};
</script>

<style scoped>
.produtos-table tr:hover:not(.v-table__expanded__content) {
  background: white !important;
}

</style>
